/**
 * FR-06 / §3.5 / residual G12 — SA JWT association at payment start (client wiring).
 *
 * Backend SoT: `SellerAssistedPaymentStartAssociationService.resolveCustomerIdForPaymentStart`
 * rebinds `session.customerId` (association-only) inside StartCustomerCheckout*Payment when
 * `authenticatedCustomerId` is present and origin is SELLER_ASSISTED_KIOSK. That is the only
 * rebind path (§3.5 / §7.3a) — do **not** bind in CheckoutHandoffPage / handoff resolve.
 *
 * Customer FE must:
 * - Keep optionalCustomerAuth (never require sign-in for guest handoff)
 * - Still send credentials/Bearer when the customer is already signed in (ApiClient)
 * - Call `prepareSellerAssistedJwtRebindAtPaymentStart` immediately before QR/gateway/cash
 *   start so intent is explicit for tests/analytics (no economics mutation on the client)
 */
import { isSellerAssistedCheckoutOrigin } from './isSellerAssistedCheckoutOrigin.js';
import type { SellerAssistedOriginSessionFields } from './isSellerAssistedCheckoutOrigin.js';

export interface SellerAssistedJwtRebindIntent {
  /** Session is SA origin — backend may rebind on payment start. */
  readonly sellerAssistedOrigin: boolean;
  /** Customer JWT / signed-in session present (optionalCustomerAuth path). */
  readonly authenticatedCustomerPresent: boolean;
  /**
   * True when payment-start UC should prefer JWT over provisional kiosk-guest.
   * Guest-without-JWT remains unchanged (FR-05/FR-06).
   */
  readonly expectAssociationRebindOnPaymentStart: boolean;
}

export function resolveSellerAssistedJwtRebindIntent(input: {
  readonly session: SellerAssistedOriginSessionFields | null | undefined;
  readonly authenticatedCustomerId?: number | null;
}): SellerAssistedJwtRebindIntent {
  const sellerAssistedOrigin = isSellerAssistedCheckoutOrigin(input.session);
  const authenticatedCustomerPresent =
    typeof input.authenticatedCustomerId === 'number' &&
    Number.isFinite(input.authenticatedCustomerId) &&
    input.authenticatedCustomerId > 0;
  return {
    sellerAssistedOrigin,
    authenticatedCustomerPresent,
    expectAssociationRebindOnPaymentStart:
      sellerAssistedOrigin && authenticatedCustomerPresent,
  };
}

/**
 * Call immediately before StartCustomerCheckout*Payment.
 * Does not mutate economics — only confirms auth credentials may travel with the request.
 * Returns the intent for tests / analytics callers.
 *
 * Rebind itself is backend-only (§3.5 / §7.3a); this helper documents FE wiring intent.
 */
export function prepareSellerAssistedJwtRebindAtPaymentStart(input: {
  readonly session: SellerAssistedOriginSessionFields | null | undefined;
  readonly authenticatedCustomerId?: number | null;
}): SellerAssistedJwtRebindIntent {
  return resolveSellerAssistedJwtRebindIntent(input);
}
